import { Injectable, computed, signal } from '@angular/core';
import { ControlService } from '../../library/control.service';
import { CallStoreService } from '../../library/call-store/call-store.service';
import { RoleplayFooterState } from 'src/app/utils/enums/learner/pathway.enum';
import { PathwayRoleplayService } from './pathway-roleplay.service';

@Injectable()
export class PathwayRoleplayFooterService {
  private readonly control = signal<ControlService | undefined>(undefined);
  private readonly store = signal<CallStoreService | undefined>(undefined);

  init(params: {
    controlService?: ControlService;
    callStore?: CallStoreService;
    defaultCaptionsOn?: boolean;
  }): void {
    const { controlService, callStore, defaultCaptionsOn = false } = params;
    this.control.set(controlService);
    this.store.set(callStore);

    if (controlService) {
      controlService.isCaptionsOn = defaultCaptionsOn;
    }
    this.captionsOn.set(controlService?.isCaptionsOn ?? defaultCaptionsOn);
    this.isVideoOn.set(controlService?.isVideoOn ?? false);
  }

  readonly state = computed<RoleplayFooterState>(() =>
    this.roleplay.footerState(),
  );
  readonly isPreamble = computed(
    () => this.state() === RoleplayFooterState.Preamble,
  );
  readonly isActive = computed(
    () => this.state() === RoleplayFooterState.Active,
  );
  readonly isResult = computed(
    () => this.state() === RoleplayFooterState.Result,
  );

  readonly actionKind = computed<'primary' | 'destructive'>(() =>
    this.isActive() ? 'destructive' : 'primary',
  );

  readonly actionLabel = computed(() => {
    if (this.isResult()) return 'call.DEBRIEF_LATEST_SESSION';
    if (this.isActive()) return 'call.END_CALL';
    return 'call.LAUNCH_SIMULATION';
  });

  readonly isActionDisabled = computed(() =>
    this.roleplay.isPrimaryActionDisabled(),
  );
  readonly captionsOn = signal<boolean>(false);

  readonly isMicOn = computed(() => this.store()?.getIsMicOn() ?? false);
  readonly isVideoOn = signal<boolean>(false);

  constructor(private readonly roleplay: PathwayRoleplayService) {}

  onCaptionsClick(): void {
    this.control()?.toggleCaptions();
    this.captionsOn.set(this.control()?.isCaptionsOn ?? false);
  }

  onMicrophoneClick(): void {
    this.control()?.toggleMic();
  }

  onVideoClick(): void {
    this.control()?.toggleVideo();
    this.isVideoOn.set(this.control()?.isVideoOn ?? false);
  }
}
